import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authoritiesAPI } from '../../api/client';
import { AGENCY_TYPES, JURISDICTION_LEVELS, RANKS } from '../../lib/emergencyConstants';
import { useAuth } from '../../context/AuthContext';
import { ArrowLeft, Edit2, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';

export default function AuthorityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isLider } = useAuth();
  const canEdit = isAdmin || isLider;
  const [authority, setAuthority] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authoritiesAPI.get(id).then(r => setAuthority(r.data)).catch(() => toast.error('Error')).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-4 border-soem-primary border-t-transparent" /></div>;
  if (!authority) return <div className="text-center py-12 text-slate-400">Autoridad no encontrada.</div>;

  const at = AGENCY_TYPES[authority.dependency_name] || AGENCY_TYPES.proteccion_civil;
  const jl = JURISDICTION_LEVELS[authority.jurisdiction_level] || { label: authority.jurisdiction_level, desc: '' };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="authority-detail">
      <div className="flex items-start gap-3">
        <button onClick={() => navigate('/dashboard/authorities')} className="soem-btn-secondary p-2 mt-1" aria-label="Volver a autoridades"><ArrowLeft size={18} /></button>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-3xl">{at.emoji}</span>
            <h1 className="text-2xl font-bold text-soem-navy">{at.label}</h1>
            <span className="bg-slate-100 text-slate-700 text-xs px-2.5 py-0.5 rounded-full font-semibold border">{jl.label}</span>
            {authority.rank && <span className="bg-white border text-slate-600 text-xs px-2 py-0.5 rounded-full font-medium">{RANKS[authority.rank]?.label || authority.rank}</span>}
            <span className="text-slate-400 text-xs font-mono">{authority.authority_code}</span>
          </div>
        </div>
        {canEdit && (
          <button onClick={() => navigate(`/dashboard/authorities/${id}/edit`)}
            className="soem-btn-secondary px-4 py-2 text-sm flex items-center gap-2">
            <Edit2 size={14} /> Editar
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          {authority.titular_name && (
            <div><p className="soem-label mb-1">Nombre del Titular</p>
              <p className="text-sm font-bold text-soem-navy text-lg">{authority.titular_name}</p></div>
          )}
          {authority.rank && (
            <div><p className="soem-label mb-1">Grado / Rango</p>
              <p className="text-sm font-medium text-soem-navy">{RANKS[authority.rank]?.label || authority.rank}</p></div>
          )}
          {authority.direct_phone && (
            <div><p className="soem-label mb-1 flex items-center gap-1"><Phone size={11} /> Teléfono directo</p>
              <p className="text-sm font-medium text-soem-navy">{authority.direct_phone}</p></div>
          )}
          {authority.institutional_email && (
            <div><p className="soem-label mb-1 flex items-center gap-1"><Mail size={11} /> Correo institucional</p>
              <p className="text-sm font-medium text-soem-navy">{authority.institutional_email}</p></div>
          )}
          {authority.notes && (
            <div><p className="soem-label mb-1">Notas</p>
              <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-sm border">{authority.notes}</p></div>
          )}
        </div>
        <div className="space-y-4">
          <div className={`p-5 rounded-sm border-2 ${at.border} ${at.bg}`}>
            <p className="soem-label mb-2">Nivel de Jurisdicción</p>
            <p className={`font-bold text-xl ${at.text}`}>{jl.label}</p>
            {jl.desc && <p className="text-sm text-slate-600 mt-1">{jl.desc}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
